// src/pages/SuperadminPage.tsx
import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  CircularProgress,
  Tabs,
  Tab,
  Button,
  IconButton,
  Tooltip,
  TablePagination,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Avatar,
  Stack,
  Card,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../contexts/NotificationContext';
import { fetchUsers, banUser, unbanUser, deleteUser, changeUserRole } from '../services/userService';
import { getAllEglises, toggleEgliseStatus, deleteEglise } from '../services/egliseService';
import { ManagedUser } from '../types/auth';
import { Eglise } from '../types/church';
import PeopleIcon from '@mui/icons-material/People';
import ChurchIcon from '@mui/icons-material/Church';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import FeedbackIcon from '@mui/icons-material/Feedback';
import DashboardIcon from '@mui/icons-material/Dashboard';
import HistoryIcon from '@mui/icons-material/History';
import GavelIcon from '@mui/icons-material/Gavel';
import CreateChurchDialog from '../components/CreateChurchDialog';
import FeedbacksView from '../components/FeedbacksView';
import AdminOverviewTab from '../components/admin/AdminOverviewTab';
import AuditLogsTab from '../components/admin/AuditLogsTab';
import ModerationQueueTab from '../components/admin/ModerationQueueTab';
import DocumentsTab from '../components/admin/DocumentsTab';
import AlarmsTab from '../components/admin/AlarmsTab';
import UserSessionsDialog from '../components/UserSessionsDialog';
import { useNavigate } from 'react-router-dom';
import DevicesIcon from '@mui/icons-material/Devices';
import FolderIcon from '@mui/icons-material/Folder';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`superadmin-tabpanel-${index}`}
      aria-labelledby={`superadmin-tab-${index}`}
      style={{ overflow: 'visible' }}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3, pb: 2 }}>{children}</Box>}
    </div>
  );
}

const SuperadminPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { success, error: showError } = useNotification();

  const [tabValue, setTabValue] = useState(0);

  // Users state
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<ManagedUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [userSearch, setUserSearch] = useState('');
  const [userPage, setUserPage] = useState(0);
  const [userRowsPerPage, setUserRowsPerPage] = useState(10);

  // Churches state
  const [churches, setChurches] = useState<Eglise[]>([]);
  const [filteredChurches, setFilteredChurches] = useState<Eglise[]>([]);
  const [loadingChurches, setLoadingChurches] = useState(false);
  const [churchSearch, setChurchSearch] = useState('');
  const [churchPage, setChurchPage] = useState(0);
  const [churchRowsPerPage, setChurchRowsPerPage] = useState(10);

  // Dialog state
  const [createChurchOpen, setCreateChurchOpen] = useState(false);

  // Action menu state
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<ManagedUser | null>(null);
  const [churchMenuAnchor, setChurchMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedChurch, setSelectedChurch] = useState<Eglise | null>(null);

  // Confirm dialogs
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    type: 'ban' | 'unban' | 'deleteUser' | 'toggleChurch' | 'deleteChurch' | 'promoteUser' | 'demoteUser';
    title: string;
    message: string;
  }>({ open: false, type: 'ban', title: '', message: '' });
  const [actionLoading, setActionLoading] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [sessionsDialogOpen, setSessionsDialogOpen] = useState(false);
  const [sessionsUser, setSessionsUser] = useState<ManagedUser | null>(null);

  // Sorting state
  const [userSortField, setUserSortField] = useState<'name' | 'email' | 'role' | 'createdAt'>('name');
  const [userSortDirection, setUserSortDirection] = useState<'asc' | 'desc'>('asc');
  const [churchSortField, setChurchSortField] = useState<'nom' | 'user_count' | 'event_count'>('nom');
  const [churchSortDirection, setChurchSortDirection] = useState<'asc' | 'desc'>('asc');

  // CSV Export for users
  const exportUsersToCSV = () => {
    if (filteredUsers.length === 0) return;
    const headers = ['Nom', 'Email', 'Rôle', 'Statut', 'Date Création'];
    const csvRows = [headers.join(',')];
    filteredUsers.forEach(user => {
      const status = user.is_banned ? 'Banni' : user.status;
      const row = [
        `"${user.name.replace(/"/g, '""')}"`,
        `"${user.email}"`,
        `"${user.role}"`,
        `"${status}"`,
        `"${user.createdAt}"`,
      ];
      csvRows.push(row.join(','));
    });
    const csvContent = csvRows.join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `utilisateurs_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // CSV Export for churches
  const exportChurchesToCSV = () => {
    if (filteredChurches.length === 0) return;
    const headers = ['Nom', 'Email', 'Adresse', 'Membres', 'Événements', 'Statut'];
    const csvRows = [headers.join(',')];
    filteredChurches.forEach(church => {
      const row = [
        `"${church.nom.replace(/"/g, '""')}"`,
        `"${church.email || ''}"`,
        `"${(church.address || '').replace(/"/g, '""')}"`,
        church.user_count,
        church.event_count,
        church.is_active ? 'Active' : 'Inactive',
      ];
      csvRows.push(row.join(','));
    });
    const csvContent = csvRows.join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `eglises_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Sort users
  const sortedFilteredUsers = [...filteredUsers].sort((a, b) => {
    let aVal: string | number = '';
    let bVal: string | number = '';
    switch (userSortField) {
      case 'name': aVal = a.name.toLowerCase(); bVal = b.name.toLowerCase(); break;
      case 'email': aVal = a.email.toLowerCase(); bVal = b.email.toLowerCase(); break;
      case 'role': aVal = a.role.toLowerCase(); bVal = b.role.toLowerCase(); break;
      case 'createdAt': aVal = a.createdAt; bVal = b.createdAt; break;
    }
    if (aVal < bVal) return userSortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return userSortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Sort churches
  const sortedFilteredChurches = [...filteredChurches].sort((a, b) => {
    let aVal: string | number = '';
    let bVal: string | number = '';
    switch (churchSortField) {
      case 'nom': aVal = a.nom.toLowerCase(); bVal = b.nom.toLowerCase(); break;
      case 'user_count': aVal = a.user_count; bVal = b.user_count; break;
      case 'event_count': aVal = a.event_count; bVal = b.event_count; break;
    }
    if (aVal < bVal) return churchSortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return churchSortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleUserSort = (field: 'name' | 'email' | 'role' | 'createdAt') => {
    if (userSortField === field) {
      setUserSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setUserSortField(field);
      setUserSortDirection('asc');
    }
  };

  const handleChurchSort = (field: 'nom' | 'user_count' | 'event_count') => {
    if (churchSortField === field) {
      setChurchSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setChurchSortField(field);
      setChurchSortDirection('asc');
    }
  };

  const UserSortIcon = ({ field }: { field: string }) => {
    if (userSortField !== field) return null;
    return userSortDirection === 'asc'
      ? <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
      : <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5 }} />;
  };

  const ChurchSortIcon = ({ field }: { field: string }) => {
    if (churchSortField !== field) return null;
    return churchSortDirection === 'asc'
      ? <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
      : <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5 }} />;
  };

  useEffect(() => {
    fetchUsersData();
    fetchChurchesData();
  }, []);

  // Filter users when search changes
  useEffect(() => {
    if (userSearch) {
      const filtered = users.filter(
        (u) =>
          u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
          u.email.toLowerCase().includes(userSearch.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
    setUserPage(0);
  }, [userSearch, users]);

  // Filter churches when search changes
  useEffect(() => {
    if (churchSearch) {
      const filtered = churches.filter(
        (c) =>
          c.nom.toLowerCase().includes(churchSearch.toLowerCase()) ||
          (c.email && c.email.toLowerCase().includes(churchSearch.toLowerCase()))
      );
      setFilteredChurches(filtered);
    } else {
      setFilteredChurches(churches);
    }
    setChurchPage(0);
  }, [churchSearch, churches]);

  const fetchUsersData = async () => {
    setLoadingUsers(true);
    try {
      const userList = await fetchUsers();
      setUsers(userList);
      setFilteredUsers(userList);
    } catch (err: any) {
      console.error('Failed to fetch users:', err);
      showError(err.response?.data?.detail || t('superadmin.users.errorFetching'));
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchChurchesData = async () => {
    setLoadingChurches(true);
    try {
      const churchList = await getAllEglises();
      setChurches(churchList);
      setFilteredChurches(churchList);
    } catch (err: any) {
      console.error('Failed to fetch churches:', err);
      showError(err.response?.data?.detail || t('superadmin.churches.errorFetching'));
    } finally {
      setLoadingChurches(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCreateChurchSuccess = () => {
    setCreateChurchOpen(false);
    fetchChurchesData();
  };

  const handleViewChurch = (churchId: number) => {
    navigate(`/churches/${churchId}`);
  };

  // User actions
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>, user: ManagedUser) => {
    setUserMenuAnchor(event.currentTarget);
    setSelectedUser(user);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
    setSelectedUser(null);
  };

  const handleBanUser = () => {
    if (!selectedUser) return;
    setBanReason('');
    setConfirmDialog({
      open: true,
      type: 'ban',
      title: t('superadmin.users.banConfirmTitle'),
      message: t('superadmin.users.banConfirmMessage', { name: selectedUser.name }),
    });
    handleUserMenuClose();
  };

  const handleUnbanUser = () => {
    if (!selectedUser) return;
    setConfirmDialog({
      open: true,
      type: 'unban',
      title: t('superadmin.users.unbanConfirmTitle'),
      message: t('superadmin.users.unbanConfirmMessage', { name: selectedUser.name }),
    });
    handleUserMenuClose();
  };

  const handleDeleteUser = () => {
    if (!selectedUser) return;
    setConfirmDialog({
      open: true,
      type: 'deleteUser',
      title: t('superadmin.users.deleteConfirmTitle'),
      message: t('superadmin.users.deleteConfirmMessage', { name: selectedUser.name }),
    });
    handleUserMenuClose();
  };

  const handlePromoteUser = () => {
    if (!selectedUser) return;
    setConfirmDialog({
      open: true,
      type: 'promoteUser',
      title: t('superadmin.users.promoteConfirmTitle'),
      message: t('superadmin.users.promoteConfirmMessage', { name: selectedUser.name }),
    });
    handleUserMenuClose();
  };

  const handleDemoteUser = () => {
    if (!selectedUser) return;
    setConfirmDialog({
      open: true,
      type: 'demoteUser',
      title: t('superadmin.users.demoteConfirmTitle'),
      message: t('superadmin.users.demoteConfirmMessage', { name: selectedUser.name }),
    });
    handleUserMenuClose();
  };

  const handleViewSessions = () => {
    if (!selectedUser) return;
    setSessionsUser(selectedUser);
    setSessionsDialogOpen(true);
    handleUserMenuClose();
  };

  // Church actions
  const handleChurchMenuOpen = (event: React.MouseEvent<HTMLElement>, church: Eglise) => {
    setChurchMenuAnchor(event.currentTarget);
    setSelectedChurch(church);
  };

  const handleChurchMenuClose = () => {
    setChurchMenuAnchor(null);
    setSelectedChurch(null);
  };

  const handleToggleChurchStatus = () => {
    if (!selectedChurch) return;
    setConfirmDialog({
      open: true,
      type: 'toggleChurch',
      title: selectedChurch.is_active
        ? t('superadmin.churches.deactivateConfirmTitle')
        : t('superadmin.churches.activateConfirmTitle'),
      message: selectedChurch.is_active
        ? t('superadmin.churches.deactivateConfirmMessage', { name: selectedChurch.nom })
        : t('superadmin.churches.activateConfirmMessage', { name: selectedChurch.nom }),
    });
    handleChurchMenuClose();
  };

  const handleDeleteChurch = () => {
    if (!selectedChurch) return;
    setConfirmDialog({
      open: true,
      type: 'deleteChurch',
      title: t('superadmin.churches.deleteConfirmTitle'),
      message: t('superadmin.churches.deleteConfirmMessage', { name: selectedChurch.nom }),
    });
    handleChurchMenuClose();
  };

  const handleConfirmAction = async () => {
    setActionLoading(true);
    try {
      switch (confirmDialog.type) {
        case 'ban':
          if (selectedUser) await banUser(selectedUser.id, banReason || undefined);
          success(t('superadmin.users.banSuccess'));
          break;
        case 'unban':
          if (selectedUser) await unbanUser(selectedUser.id);
          success(t('superadmin.users.unbanSuccess'));
          break;
        case 'deleteUser':
          if (selectedUser) await deleteUser(selectedUser.id);
          success(t('superadmin.users.deleteSuccess'));
          break;
        case 'promoteUser':
          if (selectedUser) await changeUserRole(selectedUser.id, 'Admin');
          success(t('superadmin.users.promoteSuccess'));
          break;
        case 'demoteUser':
          if (selectedUser) await changeUserRole(selectedUser.id, 'User');
          success(t('superadmin.users.demoteSuccess'));
          break;
        case 'toggleChurch':
          if (selectedChurch) await toggleEgliseStatus(selectedChurch.id);
          success(selectedChurch?.is_active ? t('superadmin.churches.deactivateSuccess') : t('superadmin.churches.activateSuccess'));
          break;
        case 'deleteChurch':
          if (selectedChurch) await deleteEglise(selectedChurch.id);
          success(t('superadmin.churches.deleteSuccess'));
          break;
      }
      // Refresh data
      if (['ban', 'unban', 'deleteUser', 'promoteUser', 'demoteUser'].includes(confirmDialog.type)) {
        fetchUsersData();
      } else {
        fetchChurchesData();
      }
    } catch (err: any) {
      console.error('Action failed:', err);
      if (['ban', 'unban', 'deleteUser', 'promoteUser', 'demoteUser'].includes(confirmDialog.type)) {
        showError(err.response?.data?.detail || err.response?.data?.message || t('superadmin.users.actionError'));
      } else {
        showError(err.response?.data?.detail || t('superadmin.churches.actionError'));
      }
    } finally {
      setActionLoading(false);
      setConfirmDialog({ ...confirmDialog, open: false });
      setBanReason('');
      setSelectedUser(null);
      setSelectedChurch(null);
    }
  };

  const statusChip = (user: ManagedUser) => {
    // Check is_banned first (from new API)
    if (user.is_banned) {
      const banInfo = user.ban_reason
        ? `${t('superadmin.users.bannedBy')}: ${user.banned_by_name || 'Admin'}\n${t('superadmin.users.reason')}: ${user.ban_reason}`
        : user.banned_by_name
        ? `${t('superadmin.users.bannedBy')}: ${user.banned_by_name}`
        : '';
      return (
        <Tooltip title={banInfo} arrow>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444' }} />
            <Typography variant="caption" color="#ef4444" fontWeight={500}>{t('superadmin.users.banned')}</Typography>
          </Stack>
        </Tooltip>
      );
    }
    // Fallback to status field
    const statusColors: Record<string, string> = {
      Active: '#16a34a',
      Pending: '#f59e0b',
      Banned: '#ef4444',
    };
    const color = statusColors[user.status] || '#94a3b8';
    return (
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
        <Typography variant="caption" color="text.secondary" fontWeight={500}>
          {user.status === 'Active' ? t('superadmin.users.active') :
           user.status === 'Pending' ? t('superadmin.users.pending') :
           user.status === 'Banned' ? t('superadmin.users.banned') : user.status}
        </Typography>
      </Stack>
    );
  };

  const roleChip = (role: string) => {
    const roleStyles: Record<string, { bg: string; color: string }> = {
      SuperAdmin: { bg: '#fef3c7', color: '#d97706' },
      Admin: { bg: '#dbeafe', color: '#2563eb' },
      User: { bg: '#f1f5f9', color: '#64748b' },
    };
    const style = roleStyles[role] || { bg: '#f1f5f9', color: '#64748b' };
    return (
      <Typography
        variant="caption"
        sx={{
          px: 1,
          py: 0.25,
          borderRadius: 1,
          bgcolor: style.bg,
          color: style.color,
          fontWeight: 500,
        }}
      >
        {role}
      </Typography>
    );
  };

  return (
    <Box>
      {/* Header - Minimal */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <AdminPanelSettingsIcon sx={{ fontSize: 24, color: '#2563eb' }} />
          <Typography variant="h5" fontWeight={600} color="#1e293b">{t('superadmin.title')}</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">{t('superadmin.subtitle')}</Typography>
      </Stack>

      {/* Tabs */}
      <Card sx={{ borderRadius: 1.5, overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e2e8f0', overflowX: 'auto' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="superadmin tabs"
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                minHeight: 48,
                px: 2,
                color: '#64748b',
                fontSize: '0.875rem',
                '&.Mui-selected': { color: '#2563eb', fontWeight: 600 },
              },
              '& .MuiTabs-indicator': { height: 2, bgcolor: '#2563eb' },
            }}
          >
            <Tab icon={<DashboardIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={t('superadmin.tabs.overview')} />
            <Tab icon={<PeopleIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={`${t('superadmin.tabs.users')} (${users.length})`} />
            <Tab icon={<ChurchIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={`${t('superadmin.tabs.churches')} (${churches.length})`} />
            <Tab icon={<GavelIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={t('superadmin.tabs.moderation')} />
            <Tab icon={<HistoryIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={t('superadmin.tabs.auditLogs')} />
            <Tab icon={<FeedbackIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={t('superadmin.tabs.feedbacks')} />
            <Tab icon={<FolderIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={t('superadmin.tabs.documents')} />
            <Tab icon={<NotificationsActiveIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={t('superadmin.tabs.alarms')} />
          </Tabs>
        </Box>

        {/* Overview Tab */}
        <TabPanel value={tabValue} index={0}>
          <AdminOverviewTab />
        </TabPanel>

        {/* Users Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ px: 2 }}>
            {/* Search Bar with Export */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <TextField
                placeholder={t('superadmin.users.searchPlaceholder')}
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 20, color: '#94a3b8' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: 350,
                  '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadIcon sx={{ fontSize: 18 }} />}
                onClick={exportUsersToCSV}
                disabled={filteredUsers.length === 0}
                sx={{
                  borderRadius: 1.5,
                  borderColor: '#e2e8f0',
                  color: '#64748b',
                  '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' },
                }}
              >
                {t('common.export')}
              </Button>
            </Stack>

            {loadingUsers ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer sx={{ maxHeight: 'calc(100vh - 450px)', minHeight: 300 }}>
                  <Table stickyHeader sx={{ minWidth: 650 }} aria-label="users table">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 700, bgcolor: 'grey.50', cursor: 'pointer', '&:hover': { bgcolor: 'grey.100' } }}
                          onClick={() => handleUserSort('name')}
                        >
                          <Stack direction="row" alignItems="center">
                            {t('superadmin.users.name')}
                            <UserSortIcon field="name" />
                          </Stack>
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 700, bgcolor: 'grey.50', cursor: 'pointer', '&:hover': { bgcolor: 'grey.100' } }}
                          onClick={() => handleUserSort('email')}
                        >
                          <Stack direction="row" alignItems="center">
                            {t('superadmin.users.email')}
                            <UserSortIcon field="email" />
                          </Stack>
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 700, bgcolor: 'grey.50', cursor: 'pointer', '&:hover': { bgcolor: 'grey.100' } }}
                          onClick={() => handleUserSort('role')}
                        >
                          <Stack direction="row" alignItems="center">
                            {t('superadmin.users.role')}
                            <UserSortIcon field="role" />
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>{t('superadmin.users.status')}</TableCell>
                        <TableCell
                          sx={{ fontWeight: 700, bgcolor: 'grey.50', cursor: 'pointer', '&:hover': { bgcolor: 'grey.100' } }}
                          onClick={() => handleUserSort('createdAt')}
                        >
                          <Stack direction="row" alignItems="center">
                            {t('superadmin.users.createdAt')}
                            <UserSortIcon field="createdAt" />
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }} align="right">
                          {t('superadmin.users.actions')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedFilteredUsers.length > 0 ? (
                        sortedFilteredUsers
                          .slice(userPage * userRowsPerPage, userPage * userRowsPerPage + userRowsPerPage)
                          .map((user) => (
                            <TableRow
                              key={user.id}
                              sx={{
                                '&:hover': { bgcolor: 'grey.50' },
                                '&:last-child td': { borderBottom: 0 },
                              }}
                            >
                              <TableCell>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                  <Avatar
                                    src={user.avatar_url || undefined}
                                    sx={{ bgcolor: '#f1f5f9', color: '#64748b', width: 32, height: 32, borderRadius: 1, fontSize: '0.875rem' }}
                                  >
                                    {user.name.charAt(0).toUpperCase()}
                                  </Avatar>
                                  <Typography variant="body2" fontWeight={500} color="#1e293b">
                                    {user.name}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {user.email}
                                </Typography>
                              </TableCell>
                              <TableCell>{roleChip(user.role)}</TableCell>
                              <TableCell>{statusChip(user)}</TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">
                                  {user.createdAt}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleUserMenuOpen(e, user)}
                                >
                                  <MoreVertIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                            <PeopleIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                              {t('superadmin.users.noUsers')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {filteredUsers.length > 0 && (
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredUsers.length}
                    rowsPerPage={userRowsPerPage}
                    page={userPage}
                    onPageChange={(_, page) => setUserPage(page)}
                    onRowsPerPageChange={(e) => {
                      setUserRowsPerPage(parseInt(e.target.value, 10));
                      setUserPage(0);
                    }}
                    labelRowsPerPage={t('common.rowsPerPage')}
                    sx={{ borderTop: 1, borderColor: 'grey.200' }}
                  />
                )}
              </>
            )}
          </Box>
        </TabPanel>

        {/* Churches Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ px: 2 }}>
            {/* Header with Search, Export, and Create Button */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <TextField
                placeholder={t('superadmin.churches.searchPlaceholder')}
                value={churchSearch}
                onChange={(e) => setChurchSearch(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 20, color: '#94a3b8' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: 300,
                  '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                }}
              />
              <Stack direction="row" spacing={1.5}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownloadIcon sx={{ fontSize: 18 }} />}
                  onClick={exportChurchesToCSV}
                  disabled={filteredChurches.length === 0}
                  sx={{
                    borderRadius: 1.5,
                    borderColor: '#e2e8f0',
                    color: '#64748b',
                    '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' },
                  }}
                >
                  {t('common.export')}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateChurchOpen(true)}
                  sx={{ borderRadius: 1.5, px: 2 }}
                >
                  {t('superadmin.churches.createButton')}
                </Button>
              </Stack>
            </Stack>

            {loadingChurches ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer sx={{ maxHeight: 'calc(100vh - 450px)', minHeight: 300 }}>
                  <Table stickyHeader sx={{ minWidth: 750 }} aria-label="churches table">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 700, bgcolor: 'grey.50', cursor: 'pointer', '&:hover': { bgcolor: 'grey.100' } }}
                          onClick={() => handleChurchSort('nom')}
                        >
                          <Stack direction="row" alignItems="center">
                            {t('superadmin.churches.name')}
                            <ChurchSortIcon field="nom" />
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>{t('superadmin.churches.admin')}</TableCell>
                        <TableCell
                          sx={{ fontWeight: 700, bgcolor: 'grey.50', cursor: 'pointer', '&:hover': { bgcolor: 'grey.100' } }}
                          align="center"
                          onClick={() => handleChurchSort('user_count')}
                        >
                          <Stack direction="row" alignItems="center" justifyContent="center">
                            {t('superadmin.churches.userCount')}
                            <ChurchSortIcon field="user_count" />
                          </Stack>
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 700, bgcolor: 'grey.50', cursor: 'pointer', '&:hover': { bgcolor: 'grey.100' } }}
                          align="center"
                          onClick={() => handleChurchSort('event_count')}
                        >
                          <Stack direction="row" alignItems="center" justifyContent="center">
                            {t('superadmin.churches.eventCount')}
                            <ChurchSortIcon field="event_count" />
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>{t('superadmin.churches.status')}</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }} align="right">
                          {t('superadmin.churches.actions')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedFilteredChurches.length > 0 ? (
                        sortedFilteredChurches
                          .slice(churchPage * churchRowsPerPage, churchPage * churchRowsPerPage + churchRowsPerPage)
                          .map((church) => (
                            <TableRow
                              key={church.id}
                              sx={{
                                '&:hover': { bgcolor: 'grey.50' },
                                '&:last-child td': { borderBottom: 0 },
                              }}
                            >
                              <TableCell>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                  {church.logo_url ? (
                                    <Avatar src={church.logo_url} sx={{ width: 40, height: 40, borderRadius: 1.5 }} />
                                  ) : (
                                    <Avatar sx={{ bgcolor: '#f1f5f9', width: 40, height: 40, borderRadius: 1.5 }}>
                                      <ChurchIcon sx={{ fontSize: 22, color: '#64748b' }} />
                                    </Avatar>
                                  )}
                                  <Box>
                                    <Typography variant="body2" fontWeight={600} color="#1e293b">
                                      {church.nom}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 180 }} noWrap>
                                      {church.address || church.email || '-'}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </TableCell>
                              <TableCell>
                                {church.admin_name ? (
                                  <Box>
                                    <Typography variant="body2" fontWeight={500} color="#1e293b">
                                      {church.admin_name}
                                    </Typography>
                                    {church.admin_email && (
                                      <Typography variant="caption" color="text.secondary">
                                        {church.admin_email}
                                      </Typography>
                                    )}
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                    {t('superadmin.churches.noAdmin')}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <Stack alignItems="center" spacing={0}>
                                  <Typography variant="body2" fontWeight={600} color="#1e293b">
                                    {church.user_count}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {t('superadmin.churches.userCount').toLowerCase()}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell align="center">
                                <Stack alignItems="center" spacing={0}>
                                  <Typography variant="body2" fontWeight={600} color="#1e293b">
                                    {church.event_count}
                                  </Typography>
                                  {church.upcoming_events !== undefined && church.upcoming_events > 0 && (
                                    <Typography variant="caption" color="primary.main">
                                      {church.upcoming_events} {t('superadmin.churches.upcomingEvents')}
                                    </Typography>
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" alignItems="center" spacing={0.75}>
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      bgcolor: church.is_active ? '#16a34a' : '#94a3b8',
                                    }}
                                  />
                                  <Typography variant="body2" color={church.is_active ? '#16a34a' : 'text.secondary'} fontWeight={500}>
                                    {church.is_active ? t('superadmin.churches.active') : t('superadmin.churches.inactive')}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell align="right">
                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                  <Tooltip title={t('superadmin.churches.viewDetails')}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleViewChurch(church.id)}
                                      sx={{ color: '#2563eb' }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleChurchMenuOpen(e, church)}
                                  >
                                    <MoreVertIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                            <ChurchIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                              {t('superadmin.churches.noChurches')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {filteredChurches.length > 0 && (
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredChurches.length}
                    rowsPerPage={churchRowsPerPage}
                    page={churchPage}
                    onPageChange={(_, page) => setChurchPage(page)}
                    onRowsPerPageChange={(e) => {
                      setChurchRowsPerPage(parseInt(e.target.value, 10));
                      setChurchPage(0);
                    }}
                    labelRowsPerPage={t('common.rowsPerPage')}
                    sx={{ borderTop: 1, borderColor: 'grey.200' }}
                  />
                )}
              </>
            )}
          </Box>
        </TabPanel>

        {/* Moderation Queue Tab */}
        <TabPanel value={tabValue} index={3}>
          <ModerationQueueTab />
        </TabPanel>

        {/* Audit Logs Tab */}
        <TabPanel value={tabValue} index={4}>
          <AuditLogsTab />
        </TabPanel>

        {/* Feedbacks Tab */}
        <TabPanel value={tabValue} index={5}>
          <Box sx={{ px: 3 }}>
            <FeedbacksView />
          </Box>
        </TabPanel>

        {/* Documents Tab */}
        <TabPanel value={tabValue} index={6}>
          <DocumentsTab />
        </TabPanel>

        {/* Alarms Tab */}
        <TabPanel value={tabValue} index={7}>
          <AlarmsTab />
        </TabPanel>
      </Card>

      {/* User Actions Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        PaperProps={{ sx: { borderRadius: 1.5, minWidth: 200, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } }}
      >
        {/* View Sessions - available for all users */}
        <MenuItem onClick={handleViewSessions}>
          <ListItemIcon>
            <DevicesIcon fontSize="small" color="info" />
          </ListItemIcon>
          <ListItemText>{t('superadmin.users.viewSessions')}</ListItemText>
        </MenuItem>
        {/* Role Management - only for non-SuperAdmin users */}
        {selectedUser && selectedUser.role !== 'SuperAdmin' && (
          selectedUser.role === 'Admin' ? (
            <MenuItem onClick={handleDemoteUser}>
              <ListItemIcon>
                <PersonRemoveIcon fontSize="small" color="warning" />
              </ListItemIcon>
              <ListItemText>{t('superadmin.users.demote')}</ListItemText>
            </MenuItem>
          ) : (
            <MenuItem onClick={handlePromoteUser}>
              <ListItemIcon>
                <PersonAddIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText>{t('superadmin.users.promote')}</ListItemText>
            </MenuItem>
          )
        )}
        {/* Ban/Unban - only for non-SuperAdmin users */}
        {selectedUser && selectedUser.role !== 'SuperAdmin' && (
          selectedUser.is_banned || selectedUser.status === 'Banned' ? (
            <MenuItem onClick={handleUnbanUser}>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText>{t('superadmin.users.unban')}</ListItemText>
            </MenuItem>
          ) : (
            <MenuItem onClick={handleBanUser}>
              <ListItemIcon>
                <BlockIcon fontSize="small" color="warning" />
              </ListItemIcon>
              <ListItemText>{t('superadmin.users.ban')}</ListItemText>
            </MenuItem>
          )
        )}
        {/* Delete - only for non-SuperAdmin users */}
        {selectedUser && selectedUser.role !== 'SuperAdmin' && (
          <MenuItem onClick={handleDeleteUser} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>{t('superadmin.users.delete')}</ListItemText>
          </MenuItem>
        )}
        {/* Message for SuperAdmin users */}
        {selectedUser?.role === 'SuperAdmin' && (
          <MenuItem disabled>
            <ListItemText sx={{ color: 'text.secondary' }}>
              {t('superadmin.users.cannotModifySuperAdmin')}
            </ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Church Actions Menu */}
      <Menu
        anchorEl={churchMenuAnchor}
        open={Boolean(churchMenuAnchor)}
        onClose={handleChurchMenuClose}
        PaperProps={{ sx: { borderRadius: 1.5, minWidth: 180, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } }}
      >
        <MenuItem onClick={() => selectedChurch && handleViewChurch(selectedChurch.id)}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('superadmin.churches.viewDetails')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleToggleChurchStatus}>
          <ListItemIcon>
            {selectedChurch?.is_active ? (
              <ToggleOffIcon fontSize="small" color="warning" />
            ) : (
              <ToggleOnIcon fontSize="small" color="success" />
            )}
          </ListItemIcon>
          <ListItemText>
            {selectedChurch?.is_active
              ? t('superadmin.churches.deactivate')
              : t('superadmin.churches.activate')}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteChurch} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{t('superadmin.churches.delete')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => !actionLoading && setConfirmDialog({ ...confirmDialog, open: false })}
        PaperProps={{ sx: { borderRadius: 1.5, minWidth: 400 } }}
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: confirmDialog.type === 'ban' ? 2 : 0 }}>
            {confirmDialog.message}
          </DialogContentText>
          {/* Ban Reason Input */}
          {confirmDialog.type === 'ban' && (
            <TextField
              fullWidth
              label={t('superadmin.users.banReason')}
              placeholder={t('superadmin.users.banReasonPlaceholder')}
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
              multiline
              rows={2}
              sx={{ mt: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setConfirmDialog({ ...confirmDialog, open: false });
              setBanReason('');
            }}
            disabled={actionLoading}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirmAction}
            color={['deleteUser', 'deleteChurch', 'ban', 'demoteUser'].includes(confirmDialog.type) ? 'error' : 'primary'}
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Church Dialog */}
      <CreateChurchDialog
        open={createChurchOpen}
        onClose={() => setCreateChurchOpen(false)}
        onSuccess={handleCreateChurchSuccess}
      />

      {/* User Sessions Dialog */}
      {sessionsUser && (
        <UserSessionsDialog
          open={sessionsDialogOpen}
          onClose={() => {
            setSessionsDialogOpen(false);
            setSessionsUser(null);
          }}
          user={sessionsUser}
        />
      )}
    </Box>
  );
};

export default SuperadminPage;
